import { Container, Layout, SectionFull, SEO } from "components"
import { graphql } from "gatsby"
import { TimelineLite } from "gsap"
import { splitNthFirstWords } from "logic/utils"
import {
  HomeType,
  RegularTileType,
  Services,
  ServiceOrderItemType,
  ContactType,
  RentalType,
} from "model/content"
import React, { FC, FunctionComponent, useEffect, useRef } from "react"
import styled from "styled-components"
import { breakpoint } from "styles/breakpoints"
import tw from "twin.macro"
import PortfolioSection, {
  Content,
  DescribedHeadingFirstWordWhite,
} from "views/home/PortfolioSection"
import ScrollDown from "views/home/ScrollDown"
import ServicesSection from "views/home/ServicesSection"
import WhatWeDo from "views/home/WhatWeDo"
import ContactInfo from "views/contact/ContactInfo"
import ContactForm from "views/contact/ContactForm"
import { AnchorToGoTo } from "components/AnchorToGoTo"
import PhotosSlider from "components/PhotosSlider"
import {
  BreakpointsProvider,
  useBreakpoints,
} from "logic/hooks/use-breakpoints"
import { ContactSection, ContactContainer } from "../retiredPages/contact"

interface IndexProps {
  data: {
    contentfulHomeSingle: HomeType
  }
}

const Heading = styled.h1`
  ${tw`relative font-condensed text-6xl md:text-7xl text-yellow leading-none z-20 uppercase`}

  span:last-of-type {
    ${tw`text-white block w-full`}
  }

  span {
    transform: translateX(-1000px);
    opacity: 0;
  }
`

const ContactSectionHome = styled(ContactSection)`
  ${tw`lg:mb-0 lg:mt-0 lg:pb-0`};
  background-color: #f4f4f4;

  .contactInfoContainer {
    ${tw`lg:mt-0 lg:pt-12`};
  }
  .contactFormContainer {
    ${tw`lg:mt-0 lg:pt-12`};

    height: 100%;
  }
`

const HeadingContainer = styled(Container)`
  ${tw`w-full`}
`
const VideoWrapper = styled(SectionFull)`
  ${tw`relative overflow-hidden flex items-center`}
  z-index: 1;
  ${breakpoint("md")`margin-top: -8rem;`}

  ${breakpoint("lg")`
    height: 100vh;

  `}
  video {
    ${breakpoint("lg")`
      min-width: 100%;
      min-height: 100%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    `}
    object-fit: cover;
  }
`

const StyledScrollDown = styled(ScrollDown)`
  ${tw`absolute bottom-0 z-10`}
  right: 50%;
  transform: translateX(50%);
`

const PhotosSlideer: FC<{ data: { contentfulHomeSingle: HomeType } }> = ({
  data,
}) => {
  const { minTablet } = useBreakpoints()

  return (
    <PhotosSlider
      photosCountToShow={minTablet ? 3 : 1}
      photosCountToMoveOnClick={minTablet ? 3 : 1}
      photos={data?.contentfulHomeSingle?.rentals.map((rental: RentalType) => ({
        photo: rental.image,
        heading: rental.title,
        description: rental.description,
      }))}
    />
  )
}
const IndexPage: FunctionComponent<IndexProps> = ({ data }) => {
  const home = data.contentfulHomeSingle
  const videoRef = useRef(null)
  const headingFirstPartRef = useRef(null)
  const headingSecondPartRef = useRef(null)
  const [headingFirstPart, headingSecondPart] = splitNthFirstWords(
    data.contentfulHomeSingle.heading,
    2
  )

  useEffect(() => {
    new TimelineLite().to(videoRef.current, {
      yPercent: 20,
      scrollTrigger: {
        trigger: videoRef.current,
        start: "bottom 90%",
        end: "bottom 0",
        scrub: true,
      },
    })
    new TimelineLite()
      .to(headingFirstPartRef.current, {
        x: 0,
        autoAlpha: 1,
        duration: 0.5,
        delay: 1,
        ease: "back.out(1.7)",
      })
      .to(headingSecondPartRef.current, {
        x: 0,
        autoAlpha: 1,
        duration: 1,
      })
  }, [])

  return (
    <Layout>
      <SEO />
      <VideoWrapper>
        {typeof window !== "undefined" && (
          <video
            src={home.video.file.url}
            loop
            autoPlay
            muted
            ref={videoRef}
            preload="auto"
          />
        )}
        <HeadingContainer>
          <Heading>
            <span ref={headingFirstPartRef}>{headingFirstPart}</span>
            <span ref={headingSecondPartRef}>{headingSecondPart}</span>
          </Heading>
        </HeadingContainer>
        <StyledScrollDown />
      </VideoWrapper>
      <WhatWeDo services={home.whatWeDoTiles} />
      <ServicesSection items={data?.contentfulHomeSingle?.services} />
      <Container>
        <Content>
          <AnchorToGoTo id="rentals" />
          <DescribedHeadingFirstWordWhite
            title={data?.contentfulHomeSingle?.rentalsSectionTitle}
            description={data?.contentfulHomeSingle?.rentalsSectionDescription}
          />
        </Content>
      </Container>
      <PhotosSlideer data={data} />
      <PortfolioSection
        title={data?.contentfulHomeSingle?.ourWorkSectionTitle}
        description={data?.contentfulHomeSingle?.ourWorkSectionDescription}
        portfolios={data?.contentfulHomeSingle?.events}
      />
      <ContactSectionHome>
        <AnchorToGoTo id="contact" />
        <ContactContainer>
          <ContactInfo {...data?.contentfulHomeSingle?.contact} />
          <ContactForm />
        </ContactContainer>
      </ContactSectionHome>
    </Layout>
  )
}

export default IndexPage

export const query = graphql`
  query IndexPage($language: String) {
    contentfulHomeSingle(node_locale: { eq: $language }) {
      heading
      video {
        file {
          url
        }
      }
      whatWeDoTiles {
        ... on ContentfulQuoteTile {
          id
          text
        }
        ... on ContentfulTile {
          id
          heading
          description
        }
      }
      services {
        ... on ContentfulService {
          title
          heading
          image {
            fluid(maxWidth: 900, quality: 100) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          description
        }
      }
      rentalsSectionTitle
      rentalsSectionDescription
      rentals {
        title
        description
        image {
          fluid(maxWidth: 800, quality: 100) {
            ...GatsbyContentfulFluid_withWebp
          }
        }
      }
      ourWorkSectionTitle
      ourWorkSectionDescription
      events {
        ... on ContentfulPortfolioItem {
          heading
          slug
          homepageDescription
          location
          logo {
            fixed(width: 150, quality: 100) {
              ...GatsbyContentfulFixed_withWebp
            }
          }
          image {
            fluid(maxWidth: 700, quality: 100) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
        }
      }
      contact {
        heading
        writeHeading
        writeParagraph
        locationHeading
        locationParagraph
        phoneHeading
        phone
        email
      }
    }
  }
`
